/**
 * MarkdownViewer — toggle between rendered preview and raw Markdown text.
 */

import React, { useState } from "react";
import ReactMarkdown from "react-markdown";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { tomorrow, oneLight } from "react-syntax-highlighter/dist/esm/styles/prism";
import remarkGfm from "remark-gfm";
import { useTheme } from "../../hooks/useTheme";

interface MarkdownViewerProps {
  markdown: string;
}

/**
 * Custom paragraph renderer: when a <p> contains only <img> elements,
 * wrap them in a flex image-group div instead of a paragraph.
 * This avoids block-level images being separated by paragraph margins.
 */
function MdParagraph({ children, ...props }: React.HTMLAttributes<HTMLElement>) {
  const kids = React.Children.toArray(children);
  const allImages = kids.every(
    (child) =>
      React.isValidElement(child) && child.type === "img"
  );
  if (allImages && kids.length > 0) {
    return <div className="md-image-group">{children}</div>;
  }
  return <p {...props}>{children}</p>;
}

export const MarkdownViewer: React.FC<MarkdownViewerProps> = ({ markdown }) => {
  const [view, setView] = useState<"rendered" | "raw">("rendered");
  const { theme } = useTheme();

  return (
    <div className="markdown-viewer">
      <div className="markdown-viewer__tabs" role="tablist">
        <button
          role="tab"
          aria-selected={view === "rendered"}
          className={`tab-btn ${view === "rendered" ? "tab-btn--active" : ""}`}
          onClick={() => setView("rendered")}
        >
          Preview
        </button>
        <button
          role="tab"
          aria-selected={view === "raw"}
          className={`tab-btn ${view === "raw" ? "tab-btn--active" : ""}`}
          onClick={() => setView("raw")}
        >
          Raw Markdown
        </button>
      </div>

      <div className="markdown-viewer__content">
        {view === "rendered" ? (
          <div className="markdown-body">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              urlTransform={(url) => url}
              components={{
                p: MdParagraph as any,
                code({ node, className, children, ...props }) {
                  const match = /language-(\w+)/.exec(className || "");
                  const isBlock = !props.style;
                  return isBlock && match ? (
                    <SyntaxHighlighter
                      style={theme === "dark" ? tomorrow : oneLight}
                      language={match[1]}
                      PreTag="div"
                    >
                      {String(children).replace(/\n$/, "")}
                    </SyntaxHighlighter>
                  ) : (
                    <code className={className} {...props}>
                      {children}
                    </code>
                  );
                },
              }}
            >
              {markdown}
            </ReactMarkdown>
          </div>
        ) : (
          <pre className="markdown-raw">
            <code>{markdown}</code>
          </pre>
        )}
      </div>
    </div>
  );
};
